// src/pages/ResetPasswordPage.js

import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link, useParams } from 'react-router-dom';
import UrlConfig from '../../data/UrlConfig';

const translations = {
  en: {
    title: 'Set New Password',
    newPasswordLabel: 'New Password',
    confirmPasswordLabel: 'Confirm New Password',
    updating: 'Updating...',
    savePassword: 'Save Password',
    successTitle: 'Password Updated!',
    successLine1: 'Your password has been changed successfully.',
    successLine2: 'You will be redirected to the login page in a few seconds...',
    invalidTokenTitle: 'Invalid Token',
    invalidTokenMessage: 'No reset token was provided.',
    requestNewLink: 'Request a new link',
    fillAllFields: 'Please complete both fields.',
    passwordsDoNotMatch: 'Passwords do not match.',
    passwordTooShort: 'Password must be at least 8 characters long.',
  },
  es: {
    title: 'Establecer Nueva Contraseña',
    newPasswordLabel: 'Nueva Contraseña',
    confirmPasswordLabel: 'Confirmar Nueva Contraseña',
    updating: 'Actualizando...',
    savePassword: 'Guardar Contraseña',
    successTitle: '¡Contraseña Actualizada!',
    successLine1: 'Tu contraseña ha sido cambiada exitosamente.',
    successLine2: 'Serás redirigido a la página de inicio de sesión en unos segundos...',
    invalidTokenTitle: 'Token Inválido',
    invalidTokenMessage: 'No se proporcionó un token de reseteo.',
    requestNewLink: 'Solicitar un nuevo enlace',
    fillAllFields: 'Por favor, completa ambos campos.',
    passwordsDoNotMatch: 'Las contraseñas no coinciden.',
    passwordTooShort: 'La contraseña debe tener al menos 8 caracteres.',
  },
};

const ResetPasswordPage = () => {
  const { lang } = useParams();
  const t = translations[lang] || translations.es;
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) { setError(t.fillAllFields); return; }
    if (password !== confirmPassword) { setError(t.passwordsDoNotMatch); return; }
    if (password.length < 8) { setError(t.passwordTooShort); return; }
    
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(UrlConfig.auth_password_reset, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, lang }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      
      setIsSuccess(true);
      setTimeout(() => navigate(`/${lang}/login?reset=success`), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-black flex flex-col justify-center items-center p-4 text-white text-center">
         <h1 className="text-2xl font-bold text-red-500">{t.invalidTokenTitle}</h1>
         <p className="text-gray-400 mt-2">{t.invalidTokenMessage}</p>
         <Link to={`/${lang}/forgot-password`} className="mt-4 font-bold text-yellow-400">{t.requestNewLink}</Link>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-black flex flex-col justify-center items-center p-4 text-white text-center">
        <h1 className="text-3xl font-bold text-green-400">{t.successTitle}</h1>
        <p className="text-gray-300 mt-4">{t.successLine1}</p>
        <p className="text-gray-400 mt-2">{t.successLine2}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center items-center p-4">
      <div className="w-full max-w-md bg-gray-900/50 border border-yellow-500/20 rounded-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-yellow-400">{t.title}</h1>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-4">
            <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="password">{t.newPasswordLabel}</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-gray-800 p-2 rounded-lg text-white" />
          </div>
          <div className="mb-6">
            <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="confirmPassword">{t.confirmPasswordLabel}</label>
            <input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="w-full bg-gray-800 p-2 rounded-lg text-white" />
          </div>
          {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}
          <button type="submit" disabled={isLoading} className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 px-4 rounded-lg disabled:bg-gray-600">
            {isLoading ? t.updating : t.savePassword}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;